<template>
  <v-card class="status-overview-card mb-4">
    <v-card-title class="d-flex align-center">
      <v-icon color="success" size="24" class="mr-2">mdi-truck-delivery</v-icon>
      DTC – Versand & Ladelisten
      <v-spacer></v-spacer>
      <v-chip color="success" size="small">
        <v-icon start>mdi-package-variant</v-icon>
        {{ verfuegbarePaletten.length }} Paletten bereit
      </v-chip>
    </v-card-title>

    <!-- Tabs: Paletten | Ladelisten | Archiv | Auswertung -->
    <v-tabs v-model="activeTab" bg-color="rgba(30, 30, 40, 0.5)" color="success">
      <v-tab value="paletten">
        <v-icon start>mdi-package-variant-closed</v-icon>
        Paletten für Versand
      </v-tab>
      <v-tab value="ladelisten">
        <v-icon start>mdi-clipboard-list</v-icon>
        Ladelisten
      </v-tab>
      <v-tab value="archiv">
        <v-icon start>mdi-archive</v-icon>
        Archiv
      </v-tab>
      <v-tab value="auswertung">
        <v-icon start>mdi-chart-bar</v-icon>
        Auswertung
      </v-tab>
    </v-tabs>

    <v-divider class="card-divider"></v-divider>

    <v-tabs-window v-model="activeTab">
      <!-- ============ TAB 1: PALETTEN ============ -->
      <v-tabs-window-item value="paletten">
        <v-card-text>
          <v-alert type="info" variant="tonal" density="compact" class="mb-4">
            <v-icon start>mdi-information</v-icon>
            Wähle Paletten aus und erstelle eine Ladeliste für den Versand nach IS SUR TILLE
          </v-alert>

          <v-data-table
            v-model="selectedPaletten"
            :headers="palettenHeaders"
            :items="verfuegbarePaletten"
            :loading="loading.paletten"
            item-value="id"
            show-select
            class="elevation-1"
          >
            <template v-slot:[`item.paletten_nr`]="{ item }">
              <strong class="text-blue-darken-1">{{ item.paletten_nr }}</strong>
            </template>
            <template v-slot:[`item.paletten_typ`]="{ item }">
              <v-chip :color="item.paletten_typ === 'A' ? 'teal' : 'blue'" size="small">{{ item.paletten_typ }}</v-chip>
            </template>
            <template v-slot:[`item.lieferschein`]="{ item }">
              <code v-if="item.lieferschein">{{ item.lieferschein }}</code>
              <span v-else class="text-grey">—</span>
            </template>
            <template v-slot:[`item.bestellnummer`]="{ item }">
              {{ item.bestellnummer || '—' }}
            </template>
            <template v-slot:[`item.anzahl_positionen`]="{ item }">
              <v-chip size="small" color="info">{{ item.anzahl_positionen }} Artikel</v-chip>
            </template>
            <template v-slot:[`item.gesamtstueckzahl`]="{ item }">
              <v-chip size="small" color="success">{{ item.gesamtstueckzahl }} Stück</v-chip>
            </template>
            <template v-slot:[`item.gesamtgewicht`]="{ item }">
              <v-chip size="small" color="orange">{{ fmtGewicht(item.gesamtgewicht) }} kg</v-chip>
            </template>
            <template v-slot:[`item.actions`]="{ item }">
              <v-btn icon="mdi-printer" size="small" variant="text" color="primary" title="Palettenschein drucken" @click="printSinglePalette(item)"></v-btn>
              <v-btn icon="mdi-eye" size="small" variant="text" color="info" title="Details anzeigen" @click="openPaletteDetails(item)"></v-btn>
              <v-btn icon="mdi-arrow-left" size="small" variant="text" color="warning" title="Zurück an Office" @click="paletteZurueckAnOffice(item)"></v-btn>
            </template>
          </v-data-table>

          <!-- Ladeliste erstellen -->
          <v-row class="mt-4">
            <v-col cols="12" md="6">
              <v-text-field
                v-model="neueLadeliste.verladedatum"
                type="date"
                label="Verladedatum"
                variant="outlined"
                bg-color="rgba(30, 30, 40, 0.5)"
                hide-details="auto"
                class="form-field"
                color="success"
                :disabled="selectedPaletten.length === 0"
              >
                <template v-slot:prepend-inner>
                  <v-icon color="success" size="small">mdi-calendar</v-icon>
                </template>
              </v-text-field>
            </v-col>
            <v-col cols="12" md="6" class="d-flex align-center">
              <v-btn
                block
                color="success"
                size="large"
                prepend-icon="mdi-plus-circle"
                :disabled="selectedPaletten.length === 0"
                :loading="loading.creating"
                @click="createLadeliste"
              >
                Ladeliste erstellen ({{ selectedPaletten.length }} Paletten)
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-tabs-window-item>

      <!-- ============ TAB 2: LADELISTEN ============ -->
      <v-tabs-window-item value="ladelisten">
        <v-card-text>
          <v-row>
            <v-col cols="12" md="4">
              <v-select
                v-model="ladelistenStatusFilter"
                :items="['alle', 'entwurf', 'verschickt']"
                label="Status"
                variant="outlined"
                bg-color="rgba(30, 30, 40, 0.5)"
                hide-details="auto"
                class="form-field"
                color="success"
                @update:model-value="loadLadelisten"
              >
                <template v-slot:prepend-inner>
                  <v-icon color="success" size="small">mdi-filter</v-icon>
                </template>
              </v-select>
            </v-col>
          </v-row>

          <v-data-table
            :headers="ladelistenHeaders"
            :items="ladelisten"
            :loading="loading.ladelisten"
            item-value="id"
            class="elevation-1"
          >
            <template v-slot:[`item.ladelisten_nr`]="{ item }">
              <strong class="text-blue-darken-1">{{ item.ladelisten_nr }}</strong>
            </template>
            <template v-slot:[`item.verladedatum`]="{ item }">
              {{ fmtDate(item.verladedatum) }}
            </template>
            <template v-slot:[`item.anzahl_paletten`]="{ item }">
              <v-chip size="small" color="info">{{ item.anzahl_paletten }} Paletten</v-chip>
            </template>
            <template v-slot:[`item.gesamtgewicht`]="{ item }">
              <v-chip size="small" color="orange">{{ fmtGewicht(item.gesamtgewicht) }} kg</v-chip>
            </template>
            <template v-slot:[`item.status`]="{ item }">
              <v-chip :color="item.status === 'verschickt' ? 'success' : 'warning'" size="small">
                <v-icon start size="small">{{ item.status === 'verschickt' ? 'mdi-check-circle' : 'mdi-pencil' }}</v-icon>
                {{ item.status === 'verschickt' ? 'Verschickt' : 'Entwurf' }}
              </v-chip>
            </template>
            <template v-slot:[`item.actions`]="{ item }">
              <v-btn icon="mdi-printer" size="small" variant="text" color="primary" title="Ladeliste drucken" @click="printLadeliste(item)"></v-btn>
              <v-btn v-if="item.status === 'entwurf'" icon="mdi-send" size="small" variant="text" color="success" title="Als verschickt markieren" @click="openVerschicken(item)"></v-btn>
              <v-btn v-if="item.status === 'entwurf'" icon="mdi-delete" size="small" variant="text" color="error" title="Ladeliste löschen" @click="confirmDelete(item)"></v-btn>
            </template>
          </v-data-table>
        </v-card-text>
      </v-tabs-window-item>

      <!-- ============ TAB 3: ARCHIV ============ -->
      <v-tabs-window-item value="archiv">
        <v-card-text>
          <v-alert type="success" variant="tonal" density="compact" class="mb-4">
            <v-icon start>mdi-archive-check</v-icon>
            Hier findest du alle abgeschlossenen Paletten mit der zugehörigen Ladeliste
          </v-alert>

          <v-data-table
            :headers="archivHeaders"
            :items="archivPaletten"
            :loading="loading.archiv"
            item-value="id"
            class="elevation-1"
          >
            <template v-slot:[`item.paletten_nr`]="{ item }">
              <strong class="text-grey-darken-2">{{ item.paletten_nr }}</strong>
            </template>
            <template v-slot:[`item.paletten_typ`]="{ item }">
              <v-chip :color="item.paletten_typ === 'A' ? 'teal' : 'blue'" size="small">{{ item.paletten_typ }}</v-chip>
            </template>
            <template v-slot:[`item.ladelisten_nr`]="{ item }">
              <v-chip v-if="item.ladelisten_nr" color="success" size="small">
                <v-icon start size="small">mdi-file-document</v-icon>
                {{ item.ladelisten_nr }}
              </v-chip>
              <span v-else class="text-grey">—</span>
            </template>
            <template v-slot:[`item.verladedatum`]="{ item }">
              {{ item.verladedatum ? fmtDate(item.verladedatum) : '–' }}
            </template>
            <template v-slot:[`item.anzahl_positionen`]="{ item }">
              <v-chip size="small" color="info">{{ item.anzahl_positionen }} Artikel</v-chip>
            </template>
            <template v-slot:[`item.gesamtgewicht`]="{ item }">
              {{ fmtGewicht(item.gesamtgewicht) }} kg
            </template>
            <template v-slot:[`item.versand_abschluss_datum`]="{ item }">
              {{ item.versand_abschluss_datum ? fmtDate(item.versand_abschluss_datum) : '–' }}
            </template>
            <template v-slot:[`item.actions`]="{ item }">
              <v-btn icon="mdi-printer" size="small" variant="text" color="primary" title="Palettenschein drucken" @click="printSinglePalette(item)"></v-btn>
            </template>
          </v-data-table>
        </v-card-text>
      </v-tabs-window-item>

      <!-- ============ TAB 4: AUSWERTUNG ============ -->
      <v-tabs-window-item value="auswertung">
        <v-card-text>
          <v-alert type="info" variant="tonal" density="compact" class="mb-4">
            <v-icon start>mdi-chart-bar</v-icon>
            Versandstatistiken im Überblick
          </v-alert>

          <v-row class="mb-2">
            <v-col cols="12" md="3">
              <v-card color="blue-lighten-5">
                <v-card-text>
                  <div class="text-overline">Versandbereit</div>
                  <div class="text-h4 font-weight-bold text-blue-darken-2">{{ verfuegbarePaletten.length }}</div>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12" md="3">
              <v-card color="orange-lighten-5">
                <v-card-text>
                  <div class="text-overline">Ladelisten (Entwurf)</div>
                  <div class="text-h4 font-weight-bold text-orange-darken-2">{{ anzahlEntwuerfe }}</div>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12" md="3">
              <v-card color="green-lighten-5">
                <v-card-text>
                  <div class="text-overline">Verschickte Ladelisten</div>
                  <div class="text-h4 font-weight-bold text-green-darken-2">{{ anzahlVerschickt }}</div>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12" md="3">
              <v-card color="purple-lighten-5">
                <v-card-text>
                  <div class="text-overline">Abgeschlossene Paletten</div>
                  <div class="text-h4 font-weight-bold text-purple-darken-2">{{ archivPaletten.length }}</div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>
      </v-tabs-window-item>
    </v-tabs-window>

    <!-- ===== Dialog: Ladeliste erstellen ===== -->
    <v-dialog v-model="dialogs.create" max-width="500">
      <v-card>
        <v-card-title>Neue Ladeliste</v-card-title>
        <v-card-text>
          <p class="text-body-2 mb-3">{{ selectedPaletten.length }} Palette(n) werden zur Ladeliste hinzugefügt.</p>
          <v-text-field
            v-model="neueLadeliste.verladedatum"
            label="Verladedatum"
            type="date"
            density="compact"
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="dialogs.create = false">Abbrechen</v-btn>
          <v-btn color="success" :loading="loading.creating" @click="createLadeliste">Erstellen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ===== Dialog: Verschicken ===== -->
    <v-dialog v-model="dialogs.verschicken" max-width="500">
      <v-card v-if="currentLadeliste">
        <v-card-title>Ladeliste verschicken</v-card-title>
        <v-card-text>
          <p class="text-body-2 mb-3">
            Ladeliste <strong>{{ currentLadeliste.ladelisten_nr }}</strong> als verschickt markieren?
            Alle enthaltenen Paletten werden abgeschlossen.
          </p>
          <v-text-field
            v-model="fahrerName"
            label="Fahrer (optional)"
            density="compact"
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="dialogs.verschicken = false">Abbrechen</v-btn>
          <v-btn color="success" :loading="loading.verschicken" @click="markVerschickt">Verschicken</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ===== Dialog: Palette Details ===== -->
    <v-dialog v-model="dialogs.details" max-width="950">
      <v-card v-if="selectedPaletteDetails">
        <v-card-title class="d-flex align-center">
          <v-avatar :color="selectedPaletteDetails.paletten_typ === 'A' ? 'teal' : 'blue'" size="32" class="mr-2">{{ selectedPaletteDetails.paletten_typ }}</v-avatar>
          {{ selectedPaletteDetails.paletten_nr }}
          <v-spacer></v-spacer>
          <v-btn icon variant="text" @click="dialogs.details = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <div class="text-caption mb-2">
            Lieferschein: {{ selectedPaletteDetails.lieferschein || '–' }} ·
            Bestellnr.: {{ selectedPaletteDetails.bestellnummer || '–' }} ·
            Gewicht: {{ fmtGewicht(selectedPaletteDetails.gesamtgewicht) }} kg
          </div>
          <v-data-table :headers="posHeaders" :items="selectedPaletteArtikel" density="compact" :items-per-page="15">
            <template v-slot:[`item.cmmf`]="{ item }">
              <v-icon v-if="istErzeugung(item)" color="orange" size="x-small" class="mr-1">mdi-asterisk</v-icon>
              <span :class="{ 'irmaf-pos': istErzeugung(item) }">{{ stripCmmf(item.cmmf) }}</span>
            </template>
          </v-data-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" variant="text" @click="printSinglePalette(selectedPaletteDetails)">
            <v-icon start>mdi-printer</v-icon> Palettenschein drucken
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ===== Dialog: Löschen ===== -->
    <v-dialog v-model="dialogs.delete" max-width="420">
      <v-card v-if="currentLadeliste">
        <v-card-title>Ladeliste löschen?</v-card-title>
        <v-card-text>
          Entwurf <strong>{{ currentLadeliste.ladelisten_nr }}</strong> wird gelöscht.
          Die Paletten werden wieder als versandbereit freigegeben.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="dialogs.delete = false">Abbrechen</v-btn>
          <v-btn color="error" @click="deleteLadeliste">Löschen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import axios from 'axios';
import { useToast } from 'vue-toastification';
import { API_BASE_URL } from '@/config';

const toast = useToast();
const API = `${API_BASE_URL}dtc/index.php`;

const activeTab = ref('paletten');

const verfuegbarePaletten = ref([]);
const selectedPaletten = ref([]);
const ladelisten = ref([]);
const ladelistenStatusFilter = ref('alle');
const archivPaletten = ref([]);
const currentLadeliste = ref(null);
const selectedPaletteDetails = ref(null);
const selectedPaletteArtikel = ref([]);
const fahrerName = ref('');

const neueLadeliste = ref({ verladedatum: new Date().toISOString().split('T')[0] });

const loading = ref({ paletten: false, ladelisten: false, creating: false, verschicken: false, archiv: false });
const dialogs = ref({ create: false, verschicken: false, details: false, delete: false });

const palettenHeaders = [
  { title: 'Paletten-Nr.', key: 'paletten_nr' },
  { title: 'Typ', key: 'paletten_typ' },
  { title: 'Lieferschein', key: 'lieferschein' },
  { title: 'Bestellnr.', key: 'bestellnummer' },
  { title: 'Artikel', key: 'anzahl_positionen' },
  { title: 'Stückzahl', key: 'gesamtstueckzahl' },
  { title: 'Gewicht', key: 'gesamtgewicht' },
  { title: 'Aktionen', key: 'actions', sortable: false }
];

const ladelistenHeaders = [
  { title: 'Ladelisten-Nr.', key: 'ladelisten_nr' },
  { title: 'Verladedatum', key: 'verladedatum' },
  { title: 'Paletten', key: 'anzahl_paletten' },
  { title: 'Gewicht', key: 'gesamtgewicht' },
  { title: 'Status', key: 'status' },
  { title: 'Aktionen', key: 'actions', sortable: false }
];

const archivHeaders = [
  { title: 'Paletten-Nr.', key: 'paletten_nr' },
  { title: 'Typ', key: 'paletten_typ' },
  { title: 'Ladeliste', key: 'ladelisten_nr' },
  { title: 'Verladedatum', key: 'verladedatum' },
  { title: 'Lieferschein', key: 'lieferschein' },
  { title: 'Artikel', key: 'anzahl_positionen' },
  { title: 'Gewicht', key: 'gesamtgewicht' },
  { title: 'Abgeschlossen', key: 'versand_abschluss_datum' },
  { title: '', key: 'actions', sortable: false }
];

const posHeaders = [
  { title: 'Weborder', key: 'weborder_id' },
  { title: 'SKU', key: 'sku' },
  { title: 'CMMF', key: 'cmmf' },
  { title: 'Tracking', key: 'tracking_number' },
  { title: 'Grund', key: 'return_reason_code' },
  { title: 'Menge', key: 'menge' }
];

const anzahlEntwuerfe = computed(() => ladelisten.value.filter(l => l.status === 'entwurf').length);
const anzahlVerschickt = computed(() => ladelisten.value.filter(l => l.status === 'verschickt').length);

// ---------- Helpers ----------
function stripCmmf(v) { return String(v ?? '').replace(/^0+/, ''); }
function istErzeugung(pos) { return parseInt(pos?.kein_output) === 1; }
function fmtGewicht(g) { return parseFloat(g || 0).toFixed(2); }
function fmtDate(d) {
  if (!d) return '–';
  const dt = new Date(d);
  if (isNaN(dt)) return d;
  return dt.toLocaleDateString('de-DE');
}

// ---------- Laden ----------
async function loadPaletten() {
  loading.value.paletten = true;
  try {
    const res = await axios.get(API, { params: { action: 'get_versand_paletten' }, withCredentials: true });
    if (res.data.status === 'success') verfuegbarePaletten.value = res.data.paletten;
  } catch (e) { toast.error('Paletten konnten nicht geladen werden'); }
  finally { loading.value.paletten = false; }
}

async function loadLadelisten() {
  loading.value.ladelisten = true;
  try {
    const params = { action: 'get_ladelisten' };
    if (ladelistenStatusFilter.value !== 'alle') params.status = ladelistenStatusFilter.value;
    const res = await axios.get(API, { params, withCredentials: true });
    if (res.data.status === 'success') ladelisten.value = res.data.ladelisten;
  } catch (e) { toast.error('Ladelisten konnten nicht geladen werden'); }
  finally { loading.value.ladelisten = false; }
}

async function loadArchiv() {
  loading.value.archiv = true;
  try {
    const res = await axios.get(API, { params: { action: 'get_archiv_paletten' }, withCredentials: true });
    if (res.data.status === 'success') archivPaletten.value = res.data.paletten;
  } catch (e) { toast.error('Archiv konnte nicht geladen werden'); }
  finally { loading.value.archiv = false; }
}

// ---------- Paletten-Details ----------
async function openPaletteDetails(p) {
  try {
    const res = await axios.get(API, { params: { action: 'get_palette', palette_id: p.id }, withCredentials: true });
    if (res.data.status === 'success') {
      selectedPaletteDetails.value = res.data.palette;
      selectedPaletteArtikel.value = res.data.palette.positionen || [];
      dialogs.value.details = true;
    }
  } catch (e) { toast.error('Palette konnte nicht geladen werden'); }
}

async function paletteZurueckAnOffice(p) {
  try {
    const res = await axios.post(API, { action: 'palette_zurueck_office', palette_id: p.id }, { withCredentials: true });
    if (res.data.status === 'success') {
      toast.success('Palette zurück an Office');
      await loadPaletten();
    } else {
      toast.error(res.data.message || 'Fehler');
    }
  } catch (e) { toast.error(e.response?.data?.message || 'Aktion fehlgeschlagen'); }
}

// ---------- Ladeliste erstellen ----------
async function createLadeliste() {
  if (selectedPaletten.value.length === 0) return;
  loading.value.creating = true;
  try {
    const res = await axios.post(API, {
      action: 'create_ladeliste',
      verladedatum: neueLadeliste.value.verladedatum,
      palette_ids: selectedPaletten.value
    }, { withCredentials: true });
    if (res.data.status === 'success') {
      toast.success('Ladeliste erstellt');
      dialogs.value.create = false;
      selectedPaletten.value = [];
      await Promise.all([loadPaletten(), loadLadelisten()]);
      activeTab.value = 'ladelisten';
    } else {
      toast.error(res.data.message || 'Fehler');
    }
  } catch (e) { toast.error(e.response?.data?.message || 'Erstellen fehlgeschlagen'); }
  finally { loading.value.creating = false; }
}

// ---------- Verschicken ----------
function openVerschicken(l) {
  currentLadeliste.value = l;
  fahrerName.value = '';
  dialogs.value.verschicken = true;
}

async function markVerschickt() {
  loading.value.verschicken = true;
  try {
    const res = await axios.post(API, {
      action: 'mark_ladeliste_verschickt',
      ladeliste_id: currentLadeliste.value.id,
      fahrer_name: fahrerName.value
    }, { withCredentials: true });
    if (res.data.status === 'success') {
      toast.success('Ladeliste verschickt');
      dialogs.value.verschicken = false;
      await Promise.all([loadPaletten(), loadLadelisten(), loadArchiv()]);
    } else {
      toast.error(res.data.message || 'Fehler');
    }
  } catch (e) { toast.error(e.response?.data?.message || 'Aktion fehlgeschlagen'); }
  finally { loading.value.verschicken = false; }
}

// ---------- Löschen ----------
function confirmDelete(l) {
  currentLadeliste.value = l;
  dialogs.value.delete = true;
}

async function deleteLadeliste() {
  try {
    const res = await axios.post(API, {
      action: 'delete_ladeliste',
      ladeliste_id: currentLadeliste.value.id
    }, { withCredentials: true });
    if (res.data.status === 'success') {
      toast.success('Ladeliste gelöscht');
      dialogs.value.delete = false;
      await Promise.all([loadPaletten(), loadLadelisten()]);
    } else {
      toast.error(res.data.message || 'Fehler');
    }
  } catch (e) { toast.error(e.response?.data?.message || 'Löschen fehlgeschlagen'); }
}

// ---------- Druck: Adresskopf ----------
function adressBlock() {
  return `
    <table style="width:100%;margin-bottom:20px;border-collapse:collapse;">
      <tr>
        <td style="width:50%;padding:10px;border:1px solid #ccc;">
          <strong>Von:</strong><br>DHS GmbH<br>Untengönrather Str. 73<br>42655 Solingen
        </td>
        <td style="width:50%;padding:10px;border:1px solid #ccc;">
          <strong>An:</strong><br>SEB<br>Magasin Nord<br>Rue du triage<br>21120 IS SUR TILLE
        </td>
      </tr>
    </table>`;
}

function kopfBlock(titel) {
  const logoUrl = `${window.location.origin}/img/dhs-logo.svg`;
  return `
    <div style="text-align:center;margin-bottom:20px;">
      <img src="${logoUrl}" style="width:150px;height:auto;display:block;margin:0 auto 12px;" alt="DHS GmbH">
      <h1 style="margin:0;font-size:20px;">${titel}</h1>
    </div>`;
}

function openPrintWindow(title, bodyHtml) {
  const w = window.open('', '_blank', 'width=900,height=650');
  if (!w) { toast.error('Popup wurde blockiert'); return; }
  w.document.write(`
    <html><head><title>${title}</title>
    <style>
      body { font-family: Arial, sans-serif; margin: 0; }
      @media print { body { margin: 0; } }
    </style></head>
    <body>${bodyHtml}</body></html>`);
  w.document.close();
  w.focus();
  setTimeout(() => { w.print(); w.close(); }, 250);
}

// ---------- Druck: einzelne Palette (Palettenschein) ----------
async function printSinglePalette(palette) {
  try {
    const res = await axios.get(API, { params: { action: 'get_palette', palette_id: palette.id }, withCredentials: true });
    if (res.data.status !== 'success') { toast.error('Fehler beim Laden der Palette'); return; }

    const data = res.data.palette;
    const positionen = data.positionen || [];
    const gesamtMenge = positionen.reduce((s, p) => s + (parseInt(p.menge) || 0), 0);
    const hatErzeugung = positionen.some(p => istErzeugung(p));

    const rows = positionen.map(pos => `
      <tr${istErzeugung(pos) ? ' style="background:#FFF3E0;"' : ''}>
        <td style="padding:3px;border:1px solid #000;font-size:9px;">${pos.weborder_id || '—'}</td>
        <td style="padding:3px;border:1px solid #000;font-size:9px;">${pos.sku || '—'}</td>
        <td style="padding:3px;border:1px solid #000;font-size:9px;">${istErzeugung(pos) ? '* ' : ''}${stripCmmf(pos.cmmf) || '—'}</td>
        <td style="padding:3px;border:1px solid #000;font-size:9px;">${pos.tracking_number || '—'}</td>
        <td style="padding:3px;border:1px solid #000;font-size:9px;">${pos.return_reason_code || '—'}</td>
        <td style="padding:3px;border:1px solid #000;text-align:center;">${pos.menge || 0}x</td>
      </tr>`).join('');

    const html = `
      <div style="border:2px solid #000;padding:20px;margin:20px;">
        ${kopfBlock('Palettenschein – ' + (palette.paletten_nr || ''))}
        ${adressBlock()}
        <table style="width:100%;margin-bottom:20px;border-collapse:collapse;border:1px solid #000;">
          <tr><th style="background:#f0f0f0;padding:8px;border:1px solid #000;text-align:left;">Paletten-Nr.</th><td style="padding:8px;border:1px solid #000;">${palette.paletten_nr || '—'}</td></tr>
          <tr><th style="background:#f0f0f0;padding:8px;border:1px solid #000;text-align:left;">Typ</th><td style="padding:8px;border:1px solid #000;">${palette.paletten_typ || '—'}</td></tr>
          <tr><th style="background:#f0f0f0;padding:8px;border:1px solid #000;text-align:left;">Lieferschein-Nr.</th><td style="padding:8px;border:1px solid #000;">${palette.lieferschein || '—'}</td></tr>
          <tr><th style="background:#f0f0f0;padding:8px;border:1px solid #000;text-align:left;">Bestellnummer</th><td style="padding:8px;border:1px solid #000;">${palette.bestellnummer || '—'}</td></tr>
          <tr><th style="background:#f0f0f0;padding:8px;border:1px solid #000;text-align:left;">Datum</th><td style="padding:8px;border:1px solid #000;">${fmtDate(new Date().toISOString().split('T')[0])}</td></tr>
          <tr><th style="background:#f0f0f0;padding:8px;border:1px solid #000;text-align:left;">Gesamtgewicht</th><td style="padding:8px;border:1px solid #000;">${fmtGewicht(palette.gesamtgewicht)} kg</td></tr>
        </table>
        ${positionen.length > 0 ? `
        <h3 style="margin-top:20px;margin-bottom:10px;">Artikel-Liste</h3>
        <table style="width:100%;margin-bottom:10px;border-collapse:collapse;border:1px solid #000;font-size:10px;">
          <thead><tr>
            <th style="background:#f0f0f0;padding:4px;border:1px solid #000;text-align:left;">Weborder</th>
            <th style="background:#f0f0f0;padding:4px;border:1px solid #000;text-align:left;">SKU</th>
            <th style="background:#f0f0f0;padding:4px;border:1px solid #000;text-align:left;">CMMF</th>
            <th style="background:#f0f0f0;padding:4px;border:1px solid #000;text-align:left;">Tracking</th>
            <th style="background:#f0f0f0;padding:4px;border:1px solid #000;text-align:left;">Grund</th>
            <th style="background:#f0f0f0;padding:4px;border:1px solid #000;text-align:center;">Menge</th>
          </tr></thead>
          <tbody>
            ${rows}
            <tr style="background:#f0f0f0;font-weight:bold;">
              <td colspan="5" style="padding:6px;border:1px solid #000;text-align:right;">Gesamtanzahl Artikel:</td>
              <td style="padding:6px;border:1px solid #000;text-align:center;">${gesamtMenge} Stück</td>
            </tr>
          </tbody>
        </table>
        ${hatErzeugung ? `<p style="font-size:10px;color:#E65100;margin:4px 0 16px;"><strong>*</strong> = (IRMAF)</p>` : ''}
        ` : ''}
        <div style="margin-top:40px;border-top:1px solid #000;padding-top:10px;">
          <p><strong>Unterschrift Fahrer:</strong> _________________________</p>
          <p><strong>Unterschrift Empfänger:</strong> _________________________</p>
        </div>
      </div>`;

    openPrintWindow(`Palettenschein – ${palette.paletten_nr}`, html);
  } catch (e) {
    toast.error('Fehler beim Laden der Palettendaten');
  }
}

// ---------- Druck: Ladeliste ----------
async function printLadeliste(ladeliste) {
  try {
    const res = await axios.get(API, { params: { action: 'get_ladeliste_paletten', ladeliste_id: ladeliste.id }, withCredentials: true });
    if (res.data.status !== 'success') { toast.error('Fehler beim Laden der Ladeliste'); return; }

    const paletten = res.data.paletten || [];
    const gesamtGewicht = paletten.reduce((s, p) => s + (parseFloat(p.gesamtgewicht) || 0), 0);

    const rows = paletten.map((p, i) => `
      <tr>
        <td style="padding:6px;border:1px solid #000;text-align:center;">${i + 1}</td>
        <td style="padding:6px;border:1px solid #000;">${p.paletten_nr || '—'}</td>
        <td style="padding:6px;border:1px solid #000;">${p.paletten_typ || '—'}</td>
        <td style="padding:6px;border:1px solid #000;">${p.lieferschein || '—'}</td>
        <td style="padding:6px;border:1px solid #000;">${p.bestellnummer || '—'}</td>
        <td style="padding:6px;border:1px solid #000;text-align:center;">83IS</td>
        <td style="padding:6px;border:1px solid #000;text-align:right;">${fmtGewicht(p.gesamtgewicht)} kg</td>
      </tr>`).join('');

    const html = `
      <div style="border:2px solid #000;padding:20px;margin:20px;">
        ${kopfBlock('Ladeliste – ' + (ladeliste.ladelisten_nr || ''))}
        ${adressBlock()}
        <table style="width:100%;margin-bottom:20px;border-collapse:collapse;border:1px solid #000;">
          <tr><th style="background:#f0f0f0;padding:8px;border:1px solid #000;text-align:left;">Ladelisten-Nr.</th><td style="padding:8px;border:1px solid #000;">${ladeliste.ladelisten_nr || '—'}</td></tr>
          <tr><th style="background:#f0f0f0;padding:8px;border:1px solid #000;text-align:left;">Verladedatum</th><td style="padding:8px;border:1px solid #000;">${fmtDate(ladeliste.verladedatum)}</td></tr>
          <tr><th style="background:#f0f0f0;padding:8px;border:1px solid #000;text-align:left;">Anzahl Paletten</th><td style="padding:8px;border:1px solid #000;">${paletten.length}</td></tr>
          <tr><th style="background:#f0f0f0;padding:8px;border:1px solid #000;text-align:left;">Gesamtgewicht</th><td style="padding:8px;border:1px solid #000;">${gesamtGewicht.toFixed(2)} kg</td></tr>
        </table>
        <table style="width:100%;margin-bottom:20px;border-collapse:collapse;border:1px solid #000;font-size:11px;">
          <thead><tr>
            <th style="background:#f0f0f0;padding:6px;border:1px solid #000;">#</th>
            <th style="background:#f0f0f0;padding:6px;border:1px solid #000;text-align:left;">Paletten-Nr.</th>
            <th style="background:#f0f0f0;padding:6px;border:1px solid #000;text-align:left;">Typ</th>
            <th style="background:#f0f0f0;padding:6px;border:1px solid #000;text-align:left;">Lieferschein</th>
            <th style="background:#f0f0f0;padding:6px;border:1px solid #000;text-align:left;">Bestellnr.</th>
            <th style="background:#f0f0f0;padding:6px;border:1px solid #000;">Lagerort</th>
            <th style="background:#f0f0f0;padding:6px;border:1px solid #000;text-align:right;">Gewicht</th>
          </tr></thead>
          <tbody>
            ${rows}
            <tr style="background:#f0f0f0;font-weight:bold;">
              <td colspan="6" style="padding:6px;border:1px solid #000;text-align:right;">Gesamtgewicht:</td>
              <td style="padding:6px;border:1px solid #000;text-align:right;">${gesamtGewicht.toFixed(2)} kg</td>
            </tr>
          </tbody>
        </table>
        <div style="margin-top:40px;border-top:1px solid #000;padding-top:10px;">
          <p><strong>Unterschrift Fahrer:</strong> _________________________</p>
          <p><strong>Unterschrift Empfänger:</strong> _________________________</p>
        </div>
      </div>`;

    openPrintWindow(`Ladeliste ${ladeliste.ladelisten_nr}`, html);
  } catch (e) {
    toast.error('Fehler beim Laden der Ladelistendaten');
  }
}

// ---------- Tab-Wechsel: passende Daten nachladen ----------
watch(activeTab, (tab) => {
  if (tab === 'ladelisten') loadLadelisten();
  else if (tab === 'archiv') loadArchiv();
});

onMounted(() => {
  loadPaletten();
  loadLadelisten();
});
</script>

<style scoped>
.status-overview-card {
  background-color: rgba(30, 30, 35, 0.7);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  transition: all 0.3s ease;
}

.card-divider {
  opacity: 0.1;
}

.form-field {
  margin-bottom: 12px;
}

.form-field :deep(.v-field__input) {
  color: rgba(255, 255, 255, 0.85);
}

.form-field :deep(.v-field__outline) {
  color: rgba(255, 255, 255, 0.12);
}

.irmaf-pos {
  color: #ffb74d;
  font-weight: 600;
}

code {
  background: rgba(255, 255, 255, 0.1);
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.85rem;
}
</style>
